import api from './api';

export default {
  /**
   * Fetch stations.
   *
   * @return Promise<Object>
   */
  getStations(params) {
    return api()
      .get('/neris/stations', { params })
      .then(response => response.data);
  },

  /**
   * Create station.
   *
   * @return Promise<Object>
   */
  createStation(data) {
    return api()
      .post('/neris/stations', data)
      .then(response => response.data);
  },

  /**
   * Update station.
   *
   * @return Promise<Object>
   */
  updateStation(stationId, data) {
    return api()
      .put(`/neris/stations/${stationId}`, data)
      .then(response => response.data);
  },

  /**
   * Delete station.
   *
   * @return Promise<Object>
   */
  deleteStation(stationId) {
    return api()
      .delete(`/neris/stations/${stationId}`)
      .then(response => response.data);
  },

  /**
   * Get units.
   *
   * @return Promise<Object>
   */
  getUnits(stationId) {
    return api()
      .get(`/neris/stations/${stationId}/units`)
      .then(response => response.data);
  },

  /**
   * Get all units.
   *
   * @return Promise<Object>
   */
  getAllUnits() {
    return api()
      .get('/neris/units')
      .then(response => response.data);
  },

  /**
   * Update unit.
   *
   * @return Promise<Object>
   */
  updateUnit(unitId, data) {
    return api()
      .put(`/neris/units/${unitId}`, data)
      .then(response => response.data);
  },

  /**
   * Delete unit.
   *
   * @return Promise<Object>
   */
  deleteUnit(unitId) {
    return api()
      .delete(`/neris/units/${unitId}`)
      .then(response => response.data);
  },

  /**
   * Create unit.
   *
   * @return Promise<Object>
   */
  createUnit(stationId, unitData) {
    return api()
      .post(`/neris/stations/${stationId}/units`, unitData)
      .then(response => response.data);
  },

  /**
   * Fetch incident list.
   *
   * @return Promise<Object>
   */
  getIncidents(params) {
    return api()
      .get('/neris/incidents', { params })
      .then(response => response.data);
  },

  /**
     * Fetch incident structure.
     *
     * @return Promise<Object>
     */
  getIncidentStructure() {
    return api()
      .get('/neris/incidents/structure')
      .then(response => response.data);
  },

  /**
   * Fetch incident data.
   *
   * @param {String} incidentNumber - Incident number
   *
   * @return Promise<Object>
   */
  getIncidentData(incidentNumber) {
    return api()
      .get(`/neris/incidents/${incidentNumber}`)
      .then(response => response.data);
  },

  /**
   * Save incident data draft.
   *
   * @param {String} incidentNumber - Incident number
   * @param {Object} data - Incident data
   *
   * @return Promise<Object>
   */
  saveIncidentDraft(incidentNumber, data) {
    return api()
      .post(`/neris/incidents/${incidentNumber}/draft`, data)
      .then(response => response.data);
  },

  /**
   * Discard draft data.
   *
   * @param {String} incidentNumber - Incident number
   *
   * @return Promise<Object>
   */
  discardDraft(incidentNumber) {
    return api()
      .delete(`/neris/incidents/${incidentNumber}/draft`)
      .then(response => response.data);
  },

  /**
   * Save incident data.
   *
   * @param {String} incidentNumber - Incident number
   * @param {Object} data - Incident data
   *
   * @return Promise<Object>
   */
  saveIncidentData(incidentNumber, data) {
    return api()
      .post(`/neris/incidents/${incidentNumber}`, data)
      .then(response => response.data);
  },

  /**
   * Update auto export flag for incidents.
   *
   * @param {Boolean} enabled - Desired auto export state
   *
   * @return Promise<Object>
   */
  updateAutoExport(enabled) {
    return api()
      .put('/neris/incidents/auto-export', { enabled })
      .then(response => response.data);
  },

  /**
   * Fetch entity structure.
   *
   * @param {Number} clientId - Client identifier
   *
   * @return Promise<Object>
   */
  getEntityStructure(clientId) {
    return api()
      .get(`/neris/entities/structure?clientId=${clientId}`)
      .then(response => response.data);
  },

  /**
   * Fetch update entity structure.
   *
   * @return Promise<Object>
   */
  getEntityUpdateStructure() {
    return api()
      .get('/neris/entities/update-structure')
      .then(response => response.data);
  },

  /**
   * Fetch my entity data.
   *
   * @return Promise<Object>
   */
  getMyEntityData() {
    return api()
      .get('/neris/entity')
      .then(response => response.data);
  },

  /**
   * Fetch entity data.
   *
   * @param {Number} clientId - Client identifier
   *
   * @return Promise<Object>
   */
  getEntityData(clientId) {
    return api()
      .get(`/neris/entities/${clientId}`)
      .then(response => response.data);
  },

  /**
   * Create entry data.
   *
   * @param {Number} clientId - Client identifier
   * @param {Object} data - Incident data
   *
   * @return Promise<Object>
   */
  createEntityData(clientId, data) {
    return api()
      .post(`/neris/entities/${clientId}`, data)
      .then(response => response.data);
  },

  /**
   * Update entry data.
   *
   * @param {Number} clientId - Client identifier
   * @param {Object} data - Incident data
   *
   * @return Promise<Object>
   */
  updateEntityData(clientId, data) {
    return api()
      .put(`/neris/entities/${clientId}`, data)
      .then(response => response.data);
  },

  /**
   *  Integrate NERIS for client.
   *
   * @param {Number} clientId - Client identifier
   * @param {Object} data - Integration data
   *
   * @return Promise<Object>
   */
  integrateClient(clientId, data) {
    return api()
      .post(`/neris/entities/${clientId}/integrate`, data)
      .then(response => ({ status: response.status, data: response.data }));
  },

  integrateClientWithCredentials(clientId, data) {
    return api()
      .post(`/neris/entities/${clientId}/integrate-with-credentials`, data)
      .then(response => response.data);
  },
};
